import { LazyQueryHookOptions, useMutation } from '@apollo/client';
import {
    Agreement,
    CurrentSubscription,
    LegalAgreementKindEnum,
    User,
} from 'types/generated/graphql';
import {
    DoQuery,
    useQueryWhenAuthenticated,
} from 'hooks/useQueryWhenAuthenticated';
import { UserActions } from 'types';
import { EventIds, UserFailureEvents } from '../../constants';
import {
    AcceptUserAgreementsMutation,
    UserAllAccessQuery,
    UserAgreementsQuery,
    UserCreditsQuery,
    UserHasSubscriptionQuery,
    UserProfileQuery,
    UserSampleSubscriptionQuery,
    UserAllSubscriptionsQuery,
} from './user.gql';

export type ApolloUserSamplesSubscription = {
    id: string;
    plan: {
        code: string;
    };
};

export const useUserHasSubscription = (): { hasSubscription: boolean } => {
    const { data } = useQueryWhenAuthenticated({
        query: UserHasSubscriptionQuery,
        errorConfig: {
            message: 'Failed to get user all access status',
            id: EventIds.FailedGetUserHasSubscription,
        },
    });

    return { hasSubscription: Boolean(data?.userHasSubscription) };
};

export const useUserCurrentPlan = (): {
    currentPlan?: CurrentSubscription;
    loading: boolean;
} => {
    const { data, loading } = useQueryWhenAuthenticated({
        query: UserSampleSubscriptionQuery,
        errorConfig: {
            message: 'Failed to get user current plan',
            id: EventIds.FailedGetUserCurrentPlan,
        },
    });

    return { currentPlan: data?.userSamplesSubscription, loading };
};

export const useUserProfile = (): {
    userProfile?: User;
    isUserLoading: boolean;
} => {
    const { data, loading } = useQueryWhenAuthenticated({
        query: UserProfileQuery,
        errorConfig: UserFailureEvents[UserActions.GetUserProfileFailed],
    });

    return { userProfile: data?.userProfile, isUserLoading: loading };
};

export const useUserCredits = (
    options?: LazyQueryHookOptions,
): {
    userCredits?: { remaining: number };
    doQuery: DoQuery;
} => {
    const { data, doQuery } = useQueryWhenAuthenticated({
        query: UserCreditsQuery,
        errorConfig:
            UserFailureEvents[UserActions.GetUserRemainingCreditsFailed],
        options,
    });

    return {
        userCredits: data?.userCredits,
        doQuery: doQuery as DoQuery,
    };
};

export const useUserAgreements = (
    options?: LazyQueryHookOptions,
): {
    userAgreements?: Agreement[];
    doQuery: DoQuery;
} => {
    const { data, doQuery } = useQueryWhenAuthenticated({
        query: UserAgreementsQuery,
        errorConfig: UserFailureEvents[UserActions.GetUserAgreementsFailed],
        options,
    });

    return {
        userAgreements: data?.userAgreements?.agreements,
        doQuery: doQuery as DoQuery,
    };
};

// MUTATIONS

export const useAcceptUserAgreements = (
    kinds: LegalAgreementKindEnum[] = [],
) => {
    const [acceptAgreements] = useMutation(AcceptUserAgreementsMutation, {
        variables: { agreements: { kinds } },
        refetchQueries: [
            {
                query: UserAgreementsQuery,
            },
        ],
        awaitRefetchQueries: true,
    });

    return acceptAgreements;
};

export const useUserAllAccess = (): { hasAAP: boolean } => {
    const { data } = useQueryWhenAuthenticated({
        query: UserAllAccessQuery,
        errorConfig: {
            message: `Failed to get user AAP status`,
            id: EventIds.FailedGetUserAllAccessPathStatus,
        },
        options: {
            fetchPolicy: 'no-cache',
        },
    });

    return { hasAAP: !!data?.userAllAccess };
};

export const useUserAllSubscription = (): {
    allSubscription: CurrentSubscription[];
} => {
    const { data } = useQueryWhenAuthenticated({
        query: UserAllSubscriptionsQuery,
        errorConfig: {
            message: 'Failed to get user all subscription',
            id: EventIds.FailedGetUserAllSubscription,
        },
    });

    return { allSubscription: data?.userAllSubscriptions?.subscriptions };
};
